import type {
  Composition,
  DocumentReference,
  Extension,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import {
  AI_NOTE_CHECK_RESULTS_EXT,
  AI_NOTE_CHECK_PROVENANCE_EXT,
  EXT_URLS,
} from '@unify/fhir-model/constants';

// ---------------------------------------------------------------------------
// QuestionnaireResponse ↔ Appointment linkage constants
// ---------------------------------------------------------------------------

/**
 * Extension URL namespaces — the PRD canonical namespace is `https://unify.health/fhir/...`.
 * Legacy code uses `https://unify.health/fhir/...`. Both are supported for reads;
 * new writes should use the `unify.health` namespace. Full migration is tracked as a
 * cross-cutting task.
 */

/** Tag system used to link a QuestionnaireResponse to an appointment. */
export const APPOINTMENT_TAG_SYSTEM = 'https://unify.health/fhir/appointment-id';

/**
 * Practitioner extension: primary clinical supervisor (reference to Practitioner).
 * Relationship graph only — cosign gating uses `requires-cosignature` / `practitionerRequiresCosignature`.
 */
export const SUPERVISING_PRACTITIONER_EXT_URL = EXT_URLS.supervisingPractitioner;

/** True when the authoring practitioner must obtain supervisor cosignature before the note is finalized. */
export { practitionerRequiresCosignature } from '@unify/fhir-model/resources/practitioner';

/** Extension URL for provider signature stored as JSON on a QR. */
export const PROVIDER_SIGNATURE_EXT_URL = 'https://unify.health/fhir/StructureDefinition/provider-signature';

/** Extension URL for client signature stored as JSON on a QR. */
export const CLIENT_SIGNATURE_EXT_URL = 'https://unify.health/fhir/StructureDefinition/client-signature';

/** Extension URL for supervisor signature stored as JSON on a QR. */
export const SUPERVISOR_SIGNATURE_EXT_URL = 'https://unify.health/fhir/StructureDefinition/supervisor-signature';

/** FHIR StructureDefinition–style extension URL for signature IP address. */
export const SIGNATURE_IP_EXT_URL = 'https://unify.health/fhir/StructureDefinition/signature-ip';

/** FHIR StructureDefinition–style extension URL for typed signature text. */
export const SIGNATURE_TYPED_TEXT_EXT_URL = 'https://unify.health/fhir/StructureDefinition/signature-typed-text';

/**
 * Extension URL for supervision review comments (GitHub PR–style) on a QR.
 * Stored as JSON array; any screen that loads this QuestionnaireResponse can read comments.
 * @see https://www.medplum.com/docs/api/fhir/resources/questionnaireresponse — no native comments field; extensions are the standard approach.
 */
export const REVIEW_COMMENTS_EXT_URL = 'https://unify.health/fhir/supervision/review-comments';

/**
 * Extension URL for "supervisor requested edits" on a QR.
 * When set, the note is in "Needs Edits" state (provider signature was removed).
 * Cleared when the provider signs again so status returns to Ready to Sign.
 */
export const REQUESTED_EDITS_EXT_URL = 'https://unify.health/fhir/supervision/requested-edits';

/** Tag system used as fallback for "requested edits" (meta.tag) so status is correct even if extension is stripped. */
export const REQUESTED_EDITS_TAG_SYSTEM = 'https://unify.health/fhir/supervision/requested-edits';
export const REQUESTED_EDITS_TAG_CODE = 'true';

/** @deprecated PRD specifies amendments as DocumentReference resources, not QR extensions. See addendums/ directory for the production implementation. */
export const ADDENDUMS_EXT_URL = 'https://unify.health/fhir/StructureDefinition/progress-note-addendums';

/**
 * Identifier system used to link an app user (e.g. by email) to a Practitioner.
 * Must match the system used in the seed script for demo Practitioners.
 */
export const PRACTITIONER_USER_EMAIL_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/identifier/user-email';

/** Extension URL for fine-grained note lifecycle status beyond FHIR QR status. */
export const DETAILED_STATUS_EXT_URL = 'https://unify.health/fhir/detailed-status';

export type DetailedStatus =
  | 'draft'
  | 'edits-requested'
  | 'signed'
  | 'cosigned'
  | 'amended'
  | 'ai-draft'
  | 'generation-in-progress'
  | 'generation-failed';

export function getDetailedStatus(qr: { extension?: Array<{ url: string; valueCode?: string }> }): DetailedStatus | undefined {
  return qr.extension?.find(e => e.url === DETAILED_STATUS_EXT_URL)?.valueCode as DetailedStatus | undefined;
}

/** Prefer real notes over transient AI failure stubs when multiple QRs share an appointment tag. */
const PRIMARY_NOTE_STATUS_PRIORITY: Partial<Record<DetailedStatus, number>> = {
  'ai-draft': 100,
  'edits-requested': 95,
  amended: 90,
  signed: 90,
  cosigned: 90,
  draft: 80,
  'generation-in-progress': 70,
  'generation-failed': 10,
};

export function pickPrimaryAppointmentNote<T extends { response: QuestionnaireResponse }>(
  entries: T[],
): T | undefined {
  if (entries.length === 0) return undefined;
  if (entries.length === 1) return entries[0];

  const withoutFailed = entries.filter((e) => getDetailedStatus(e.response) !== 'generation-failed');
  const candidates = withoutFailed.length > 0 ? withoutFailed : entries;

  return [...candidates].sort((a, b) => {
    const pa = PRIMARY_NOTE_STATUS_PRIORITY[getDetailedStatus(a.response) ?? 'draft'] ?? 50;
    const pb = PRIMARY_NOTE_STATUS_PRIORITY[getDetailedStatus(b.response) ?? 'draft'] ?? 50;
    if (pb !== pa) return pb - pa;
    const ta = a.response.meta?.lastUpdated ?? '';
    const tb = b.response.meta?.lastUpdated ?? '';
    return tb.localeCompare(ta);
  })[0];
}

export function setDetailedStatus(extensions: Extension[], status: DetailedStatus): Extension[] {
  const filtered = extensions.filter(e => e.url !== DETAILED_STATUS_EXT_URL);
  filtered.push({ url: DETAILED_STATUS_EXT_URL, valueCode: status });
  return filtered;
}

/** Extension URL for AI-generated content provenance tracking. */
export const AI_PROVENANCE_EXT_URL = 'https://unify.health/fhir/ai-generated-provenance';

/** Extension URL for AI note quality check results JSON on QuestionnaireResponse. */
export { AI_NOTE_CHECK_RESULTS_EXT as AI_NOTE_CHECK_RESULTS_EXT_URL } from '@unify/fhir-model/constants';

/** Extension URL for AI note check provenance metadata on QuestionnaireResponse. */
export { AI_NOTE_CHECK_PROVENANCE_EXT as AI_NOTE_CHECK_PROVENANCE_EXT_URL } from '@unify/fhir-model/constants';

export type AiNoteCheckStatus = 'passed' | 'flagged' | 'review_recommended';

export type AiNoteCheckId =
  | 'required-fields'
  | 'similarity-prior'
  | 'names-in-note'
  | 'quotes-in-note';

/** One located issue within a quality check row. */
export interface NoteCheckFinding {
  location: string;
  message: string;
  itemPath?: string;
}

export interface AiNoteCheckResult {
  id: AiNoteCheckId;
  label: string;
  status: AiNoteCheckStatus;
  detail?: string;
  findings?: NoteCheckFinding[];
}

export interface AiNoteCheckAcknowledgment {
  checkId: AiNoteCheckId;
  acknowledgedAt: string;
  acknowledgedBy?: string;
  comment?: string;
}

export interface AiNoteCheckResultsPayload {
  status: 'completed' | 'failed';
  checkedAt: string;
  checkedBy: 'ai-note-checker';
  model?: string;
  modelVersion?: string;
  durationMs?: number;
  noteFingerprint: string;
  questionnaireResponseId: string;
  questionnaireResponseVersionId?: string;
  checks: AiNoteCheckResult[];
  acknowledgments?: AiNoteCheckAcknowledgment[];
  similarityComparedCount?: number;
  error?: string;
}

/** Checks that need sign-time acknowledgment (warn-only policy). */
export function getChecksRequiringAcknowledgment(checks: AiNoteCheckResult[] | undefined): AiNoteCheckResult[] {
  return (checks ?? []).filter((c) => c.status === 'flagged' || c.status === 'review_recommended');
}

export function mergeAcknowledgmentsOntoQR(
  qr: QuestionnaireResponse,
  acknowledgments: AiNoteCheckAcknowledgment[],
): QuestionnaireResponse {
  const payload = getAiNoteCheckResultsFromQR(qr);
  if (!payload) return qr;
  const byId = new Map<string, AiNoteCheckAcknowledgment>();
  for (const a of payload.acknowledgments ?? []) byId.set(a.checkId, a);
  for (const a of acknowledgments) byId.set(a.checkId, a);
  return setAiNoteCheckResultsOnQR(qr, {
    ...payload,
    acknowledgments: [...byId.values()],
  });
}

/** Read AI note check results from a QR extension. Returns undefined if missing or invalid. */
export function getAiNoteCheckResultsFromQR(qr: QuestionnaireResponse): AiNoteCheckResultsPayload | undefined {
  const ext = qr.extension?.find((e) => e.url === AI_NOTE_CHECK_RESULTS_EXT);
  if (!ext?.valueString) return undefined;
  try {
    const parsed = JSON.parse(ext.valueString) as AiNoteCheckResultsPayload;
    if (parsed?.status && Array.isArray(parsed.checks)) return parsed;
    return undefined;
  } catch {
    return undefined;
  }
}

/** Merge extensions for save: start from existing QR, overlay explicit updates by URL. */
export function mergeQuestionnaireResponseExtensionsForSave(
  existing: QuestionnaireResponse | null | undefined,
  updated: Extension[],
): Extension[] {
  const byUrl = new Map<string, Extension>();
  for (const ext of existing?.extension ?? []) {
    if (ext.url) byUrl.set(ext.url, ext);
  }
  for (const ext of updated) {
    if (ext.url) byUrl.set(ext.url, ext);
  }
  return [...byUrl.values()];
}

/** Write AI note check results onto a QR (returns shallow copy). */
export function setAiNoteCheckResultsOnQR(
  qr: QuestionnaireResponse,
  payload: AiNoteCheckResultsPayload,
): QuestionnaireResponse {
  const other = (qr.extension ?? []).filter(
    (e) => e.url !== AI_NOTE_CHECK_RESULTS_EXT && e.url !== AI_NOTE_CHECK_PROVENANCE_EXT,
  );
  return {
    ...qr,
    extension: [...other, { url: AI_NOTE_CHECK_RESULTS_EXT, valueString: JSON.stringify(payload) }],
  };
}

/**
 * Merge AI check extensions from a bot response onto the locally saved QR.
 * Preserves saved note body fields if the bot read a stale snapshot.
 */
export function mergeAiNoteCheckOntoSavedResponse(
  saved: QuestionnaireResponse,
  botResponse: QuestionnaireResponse,
): QuestionnaireResponse {
  const payload = getAiNoteCheckResultsFromQR(botResponse);
  if (!payload) return saved;
  let merged = setAiNoteCheckResultsOnQR(saved, payload);
  const provenanceExt = botResponse.extension?.find((e) => e.url === AI_NOTE_CHECK_PROVENANCE_EXT);
  if (provenanceExt) {
    const other = (merged.extension ?? []).filter((e) => e.url !== AI_NOTE_CHECK_PROVENANCE_EXT);
    merged = { ...merged, extension: [...other, provenanceExt] };
  }
  return merged;
}

/** Map persisted check results to automatic review rows for NoteQualityChecklist. */
export function getAutomaticReviewChecksFromQR(qr: QuestionnaireResponse): AiNoteCheckResult[] | undefined {
  const payload = getAiNoteCheckResultsFromQR(qr);
  if (payload?.status !== 'completed') return undefined;
  return payload.checks;
}

// ---------------------------------------------------------------------------
// QR tag helpers
// ---------------------------------------------------------------------------

/** Build a meta.tag entry linking a QR to an appointment. */
export function appointmentTag(appointmentId: string) {
  return { system: APPOINTMENT_TAG_SYSTEM, code: appointmentId };
}

/** Read the appointment ID from a QR's meta.tag. */
export function getAppointmentIdFromQR(qr: QuestionnaireResponse): string | undefined {
  return qr.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code;
}

// ---------------------------------------------------------------------------
// QR signature helpers
// ---------------------------------------------------------------------------

/** Read a signature from a QR extension. */
export function getSignatureFromQR(
  qr: QuestionnaireResponse,
  extUrl: string,
): SignatureInfo | undefined {
  const ext = qr.extension?.find((e) => e.url === extUrl);
  if (!ext?.valueString) return undefined;
  try {
    return JSON.parse(ext.valueString) as SignatureInfo;
  } catch {
    return undefined;
  }
}

/** Write a signature as an extension on a QR (returns shallow copy). */
export function setSignatureOnQR(
  qr: QuestionnaireResponse,
  extUrl: string,
  signature: SignatureInfo,
): QuestionnaireResponse {
  const existingExt = (qr.extension ?? []).find((e) => e.url === extUrl);
  if (existingExt?.valueString) {
    try {
      const parsed = JSON.parse(existingExt.valueString) as SignatureInfo;
      if (typeof parsed.signedAt === 'string' && parsed.signedAt.trim()) {
        const other = (qr.extension ?? []).filter((e) => e.url !== extUrl);
        return { ...qr, extension: [...other, existingExt] };
      }
    } catch {
      // Invalid JSON — replace with the new payload.
    }
  }
  const existingExts = (qr.extension ?? []).filter((e) => e.url !== extUrl);
  return {
    ...qr,
    extension: [...existingExts, { url: extUrl, valueString: JSON.stringify(signature) }],
  };
}

/**
 * Keep a locally persisted provider signature when a stale refetch / AI-check
 * payload arrives without one.
 */
export function withPreservedProviderSignature(
  existing: QuestionnaireResponse | undefined,
  incoming: QuestionnaireResponse,
): QuestionnaireResponse {
  const existingSig = existing ? getSignatureFromQR(existing, PROVIDER_SIGNATURE_EXT_URL) : undefined;
  if (!existingSig || getSignatureFromQR(incoming, PROVIDER_SIGNATURE_EXT_URL)) {
    return incoming;
  }
  return setSignatureOnQR(incoming, PROVIDER_SIGNATURE_EXT_URL, existingSig);
}

/**
 * Supervisor co-sign and lock: persist supervisor signature and set detailed-status to cosigned.
 * Used by the supervision document review flow for progress notes and treatment plans.
 */
export function applySupervisorCosignature(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  const withSignature = setSignatureOnQR(qr, SUPERVISOR_SIGNATURE_EXT_URL, signature);
  return {
    ...withSignature,
    extension: setDetailedStatus(withSignature.extension ?? [], 'cosigned'),
  };
}

/** Remove a signature extension from a QR (e.g. when supervisor returns note for edits). Returns shallow copy. */
export function removeSignatureFromQR(
  qr: QuestionnaireResponse,
  extUrl: string,
): QuestionnaireResponse {
  const existingExts = (qr.extension ?? []).filter((e) => e.url !== extUrl);
  return { ...qr, extension: existingExts };
}

/** Whether the QR has the "supervisor requested edits" marker (extension or meta.tag; note returned for edits). */
export function getRequestedEditsFromQR(qr: QuestionnaireResponse): boolean {
  const hasExtension = qr.extension?.some((e) => e.url === REQUESTED_EDITS_EXT_URL) ?? false;
  const hasTag =
    qr.meta?.tag?.some(
      (t) => t.system === REQUESTED_EDITS_TAG_SYSTEM && t.code === REQUESTED_EDITS_TAG_CODE,
    ) ?? false;
  return hasExtension || hasTag;
}

/** Set the "supervisor requested edits" extension and meta.tag on a QR. Returns shallow copy. */
export function setRequestedEditsOnQR(qr: QuestionnaireResponse): QuestionnaireResponse {
  const existingExts = (qr.extension ?? []).filter((e) => e.url !== REQUESTED_EDITS_EXT_URL);
  const requestedEditsTag = {
    system: REQUESTED_EDITS_TAG_SYSTEM,
    code: REQUESTED_EDITS_TAG_CODE,
  };
  const existingTags = (qr.meta?.tag ?? []).filter(
    (t) => !(t.system === REQUESTED_EDITS_TAG_SYSTEM && t.code === REQUESTED_EDITS_TAG_CODE),
  );
  return {
    ...qr,
    extension: [...existingExts, { url: REQUESTED_EDITS_EXT_URL, valueString: 'true' }],
    meta: {
      ...qr.meta,
      tag: [...existingTags, requestedEditsTag],
    },
  };
}

/** Remove the "supervisor requested edits" extension and tag (e.g. when provider signs again). Returns shallow copy. */
export function removeRequestedEditsFromQR(qr: QuestionnaireResponse): QuestionnaireResponse {
  const existingExts = (qr.extension ?? []).filter((e) => e.url !== REQUESTED_EDITS_EXT_URL);
  const existingTags = (qr.meta?.tag ?? []).filter(
    (t) => !(t.system === REQUESTED_EDITS_TAG_SYSTEM && t.code === REQUESTED_EDITS_TAG_CODE),
  );
  return {
    ...qr,
    extension: existingExts,
    meta: { ...qr.meta, tag: existingTags },
  };
}

// ---------------------------------------------------------------------------
// QR review comments (supervision document review)
// ---------------------------------------------------------------------------

/** Stored shape for a single review comment; matches supervision DocumentComment. */
export interface StoredReviewComment {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: 'supervisor' | 'clinician';
  text: string;
  createdAt: string;
  resolvedAt?: string;
  anchor?: {
    section?: string;
    lineStart?: number;
    lineEnd?: number;
    snippet?: string;
    linkId?: string;
    itemPath?: string;
  };
}

/** Read review comments from a QR extension. Returns [] if missing or invalid. */
export function getReviewCommentsFromQR(qr: QuestionnaireResponse): StoredReviewComment[] {
  const ext = qr.extension?.find((e) => e.url === REVIEW_COMMENTS_EXT_URL);
  if (!ext?.valueString) return [];
  try {
    const parsed = JSON.parse(ext.valueString) as unknown;
    return Array.isArray(parsed) ? (parsed as StoredReviewComment[]) : [];
  } catch {
    return [];
  }
}

/** Write review comments as an extension on a QR (returns shallow copy). */
export function setReviewCommentsOnQR(
  qr: QuestionnaireResponse,
  comments: StoredReviewComment[],
): QuestionnaireResponse {
  const existingExts = (qr.extension ?? []).filter((e) => e.url !== REVIEW_COMMENTS_EXT_URL);
  return {
    ...qr,
    extension: [
      ...existingExts,
      { url: REVIEW_COMMENTS_EXT_URL, valueString: JSON.stringify(comments) },
    ],
  };
}

// ---------------------------------------------------------------------------
// QR status mapping
// ---------------------------------------------------------------------------

/** Map QR FHIR status to our UI display status. */
export function qrStatusToDisplay(
  qr: QuestionnaireResponse,
): string {
  const hasSig = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
  if (hasSig) return 'Signed';
  switch (qr.status) {
    case 'in-progress':
      return 'Draft';
    case 'completed':
      return 'Completed';
    case 'amended':
      return 'Amended';
    default:
      return 'Draft';
  }
}

// ---------------------------------------------------------------------------
// QR item helpers for display rendering
// ---------------------------------------------------------------------------

/** Get display string(s) for a single QR answer. */
export function getAnswerDisplayValue(
  answer: QuestionnaireResponseItem['answer'] extends (infer A)[] | undefined ? A : never,
): string {
  if (!answer) return '';
  if (answer.valueString !== undefined) return answer.valueString;
  if (answer.valueInteger !== undefined) return String(answer.valueInteger);
  if (answer.valueDecimal !== undefined) return String(answer.valueDecimal);
  if (answer.valueBoolean !== undefined) return answer.valueBoolean ? 'Yes' : 'No';
  if (answer.valueDate) return answer.valueDate;
  if (answer.valueDateTime) return answer.valueDateTime;
  if (answer.valueCoding) return answer.valueCoding.display ?? answer.valueCoding.code ?? '';
  if (answer.valueQuantity) {
    const q = answer.valueQuantity;
    return `${q.value ?? ''}${q.unit ? ` ${q.unit}` : ''}`;
  }
  if (answer.valueReference) return answer.valueReference.display ?? answer.valueReference.reference ?? '';
  return '';
}

/** Collect all answer display values from a response item. */
export function getItemAnswerText(item: QuestionnaireResponseItem): string {
  if (!item.answer || item.answer.length === 0) return '';
  return item.answer.map(getAnswerDisplayValue).filter(Boolean).join(', ');
}

/** Look up a Questionnaire item's text by linkId (for fallback when QR item.text is missing). */
export function findQuestionnaireItemText(
  questionnaire: Questionnaire,
  linkId: string,
): string | undefined {
  function search(items: Questionnaire['item']): string | undefined {
    for (const item of items ?? []) {
      if (item.linkId === linkId) return item.text;
      const found = search(item.item);
      if (found) return found;
    }
    return undefined;
  }
  return search(questionnaire.item);
}

/**
 * UI model for a structured progress note.
 * Regardless of whether the source is a Composition or DocumentReference,
 * we normalize into this shape for consistent rendering.
 */
export interface ProgressNoteUI {
  presentingProblem?: string;
  interventions?: string;
  response?: string;
  riskAssessment?: string;
  diagnosis?: string;
  plan?: string;
  homework?: string;
  additional?: string;
}

/**
 * Signature information extracted from note resources.
 *
 * Maps conceptually to the FHIR Signature datatype:
 * - signatureData  → Signature.data   (base64 PNG, no data-url prefix)
 * - signatureFormat → Signature.sigFormat ("image/png")
 * - signedAt       → Signature.when
 * - ipAddress      → extension: SIGNATURE_IP_EXT_URL
 * - typedText      → extension: SIGNATURE_TYPED_TEXT_EXT_URL
 */
export interface SignatureInfo {
  name: string;
  title?: string;
  /** Provider license or credential (e.g. LCSW, PhD, MD). Shown under "Signed by" line. */
  license?: string;
  signedAt?: string;
  /** IP address the signature was submitted from. */
  ipAddress?: string;
  /** Base64-encoded PNG of the rendered signature (no data-url prefix). */
  signatureData?: string;
  /** MIME type of signatureData (always "image/png"). */
  signatureFormat?: string;
  /** How the signature was captured: "type" or "draw". */
  signatureMode?: 'type' | 'draw';
  /** Original typed text when signatureMode is "type" (preserved for auditability). */
  typedText?: string;
}

/**
 * Combined note data after parsing a Composition or DocumentReference.
 */
export interface ParsedNoteData {
  title: string;
  noteType: string;
  sections: ProgressNoteUI;
  providerSignature?: SignatureInfo;
  supervisorSignature?: SignatureInfo;
  status?: string;

  /**
   * When the note was created from a Questionnaire template, these fields
   * store the template id and the raw form values so the note can be
   * re-opened in the editor and rendered in a template-aware read-only view.
   */
  templateId?: string;
  formValues?: Record<string, string | string[]>;
}

// ---------------------------------------------------------------------------
// Section-title matching
// ---------------------------------------------------------------------------

/** Canonical section keys in our UI model. */
type SectionKey = keyof ProgressNoteUI;

/**
 * Map of lowercase keywords/phrases to their canonical section key.
 * Order matters: first match wins.
 */
const SECTION_MATCHERS: Array<{ key: SectionKey; patterns: string[] }> = [
  {
    key: 'presentingProblem',
    patterns: [
      'presenting problem',
      'reason for visit',
      'chief complaint',
      'subjective',
      'presenting concerns',
    ],
  },
  {
    key: 'interventions',
    patterns: [
      'intervention',
      'therapeutic intervention',
      'treatment provided',
      'clinical intervention',
    ],
  },
  {
    key: 'response',
    patterns: [
      'client response',
      'patient response',
      'progress',
      'response to treatment',
      'session progress',
      'objective',
    ],
  },
  {
    key: 'riskAssessment',
    patterns: [
      'risk assessment',
      'safety',
      'suicide',
      'self-harm',
      'risk',
      'safety plan',
    ],
  },
  {
    key: 'diagnosis',
    patterns: ['diagnosis', 'diagnostic', 'assessment', 'impression'],
  },
  {
    key: 'plan',
    patterns: [
      'treatment plan',
      'plan',
      'next steps',
      'follow-up',
      'recommendations',
    ],
  },
  {
    key: 'homework',
    patterns: [
      'homework',
      'between-session',
      'between session',
      'home practice',
      'assignments',
    ],
  },
  {
    key: 'additional',
    patterns: ['additional', 'notes', 'other', 'comments', 'mse', 'mental status'],
  },
];

function matchSectionKey(sectionTitle: string): SectionKey | undefined {
  const lower = sectionTitle.toLowerCase();
  for (const { key, patterns } of SECTION_MATCHERS) {
    if (patterns.some((p) => lower.includes(p))) {
      return key;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Composition → ProgressNoteUI
// ---------------------------------------------------------------------------

/**
 * Parse a FHIR Composition into our structured ProgressNoteUI.
 * Sections are matched by title keyword; unmatched sections are appended to `additional`.
 */
export function compositionToProgressNote(composition: Composition): ParsedNoteData {
  const sections: ProgressNoteUI = {};
  const unmatchedParts: string[] = [];

  for (const section of composition.section ?? []) {
    const title = section.title ?? '';
    const text = section.text?.div
      ? stripHtml(section.text.div)
      : '';

    const key = matchSectionKey(title);
    if (key) {
      // If there's already content for this key, append
      sections[key] = sections[key] ? `${sections[key]}\n\n${text}` : text;
    } else if (text) {
      unmatchedParts.push(`${title ? `${title}: ` : ''}${text}`);
    }
  }

  if (unmatchedParts.length > 0) {
    const existing = sections.additional ?? '';
    sections.additional = existing
      ? `${existing}\n\n${unmatchedParts.join('\n\n')}`
      : unmatchedParts.join('\n\n');
  }

  // Extract signatures from attester array
  const providerAttester = composition.attester?.find(
    (a) => a.mode === 'legal' || a.mode === 'professional'
  ) ?? composition.attester?.[0];

  const supervisorAttester = composition.attester?.find(
    (a) => a.mode === 'official'
  ) ?? (composition.attester && composition.attester.length > 1 ? composition.attester[1] : undefined);

  return {
    title: composition.title ?? 'Progress Note',
    noteType: composition.type?.text ?? composition.type?.coding?.[0]?.display ?? 'Progress Note',
    sections,
    providerSignature: providerAttester
      ? {
          name: providerAttester.party?.display ?? 'Provider',
          signedAt: providerAttester.time,
        }
      : undefined,
    supervisorSignature: supervisorAttester
      ? {
          name: supervisorAttester.party?.display ?? 'Supervisor',
          signedAt: supervisorAttester.time,
        }
      : undefined,
    status: composition.status,
  };
}

// ---------------------------------------------------------------------------
// DocumentReference → ProgressNoteUI
// ---------------------------------------------------------------------------

/**
 * Parse a FHIR DocumentReference into our ProgressNoteUI.
 * Since DocumentReference content is typically unstructured, we place everything
 * in the `additional` field. Structured section fields show as empty.
 */
export function documentReferenceToProgressNote(
  docRef: DocumentReference,
  textContent?: string,
): ParsedNoteData {
  const content = textContent
    ?? docRef.description
    ?? docRef.content?.[0]?.attachment?.title
    ?? '';

  const sections: ProgressNoteUI = {
    additional: content || undefined,
  };

  // Extract author info as provider signature
  const primaryAuthor = docRef.author?.[0];
  const supervisorAuthor = docRef.author && docRef.author.length > 1
    ? docRef.author[1]
    : undefined;

  return {
    title: docRef.description ?? 'Progress Note',
    noteType: docRef.type?.text ?? docRef.type?.coding?.[0]?.display ?? 'Clinical Document',
    sections,
    providerSignature: primaryAuthor
      ? {
          name: primaryAuthor.display ?? 'Provider',
          signedAt: docRef.date,
        }
      : undefined,
    supervisorSignature: supervisorAuthor
      ? {
          name: supervisorAuthor.display ?? 'Supervisor',
          signedAt: docRef.date,
        }
      : undefined,
    status: docRef.status,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { stripHtml } from './utils/stripHtml';

/**
 * Create an empty ParsedNoteData when no note exists.
 */
export function emptyNoteData(): ParsedNoteData {
  return {
    title: 'Progress Note',
    noteType: 'Progress Note',
    sections: {},
    status: undefined,
  };
}
